import { Injectable, signal } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { inject } from '@angular/core';
import { Internal } from '../decorators/app.decorators';
import { OutputStagedChangesService } from './output-staged-changes.service';

export type UnsavedChangesChoice = 'save' | 'discard' | 'stay';

/** Supplied by whichever output component owns the staged cluster customization. */
export interface UnsavedChangesHandler {
  /** Commits the active staged change; resolves true only when it was persisted. */
  save?: () => Promise<boolean>;
  /** Reverts the active staged change to the last saved state. */
  discard?: () => void;
}

/**
 * Gate for leaving a cluster page that still holds a recalculated-but-unsaved customization (see
 * OutputStagedChangesService). The Save/Discard/Stay dialog itself is rendered by
 * RecommendationComponent from `prompt()`; every exit path (tree-nav, tabs, view toggle, router,
 * refresh) asks `confirmLeave()` first and proceeds only when it resolves true.
 */
@Injectable({ providedIn: 'root' })
@Internal()
export class UnsavedChangesGuardService {
  private readonly saveHandlers = new Set<UnsavedChangesHandler>();
  private pending: Promise<boolean> | null = null;
  private resolveChoice: ((choice: UnsavedChangesChoice) => void) | null = null;

  readonly prompt = signal(false);
  readonly saving = signal(false);

  constructor(private staged: OutputStagedChangesService) {}

  /** Returns the same object to pass back to unregister(), so a late teardown of an older
   *  component can't drop the handler a newer one just registered. */
  register(handler: UnsavedChangesHandler): UnsavedChangesHandler {
    this.saveHandlers.add(handler);
    return handler;
  }

  unregister(handler: UnsavedChangesHandler): void {
    this.saveHandlers.delete(handler);
  }

  /** Only pages that registered a discard handler (workload/cluster and management domain pages)
   *  are guarded. */
  hasUnsaved(): boolean {
    return this.hasDiscardHandler() && this.staged.hasUnsavedRecalculation(this.staged.activeKey());
  }

  /** Resolves true when the caller may carry on leaving (nothing unsaved, saved, or discarded). */
  confirmLeave(): Promise<boolean> {
    if (this.pending) return this.pending;
    if (!this.hasUnsaved()) return Promise.resolve(true);
    this.prompt.set(true);
    this.pending = new Promise<UnsavedChangesChoice>((resolve) => {
      this.resolveChoice = resolve;
    }).then((choice) => this.settle(choice));
    return this.pending;
  }

  /** Runs `action` straight away (synchronously) when nothing is unsaved, otherwise only after the
   *  user has saved or discarded. Keeps the common no-changes path free of any async hop. */
  runIfMayLeave(action: () => void): void {
    if (!this.pending && !this.hasUnsaved()) {
      action();
      return;
    }
    this.confirmLeave().then((mayLeave) => {
      if (mayLeave) action();
    });
  }

  /** Called by the dialog's buttons. */
  choose(choice: UnsavedChangesChoice): void {
    this.resolveChoice?.(choice);
  }

  private async settle(choice: UnsavedChangesChoice): Promise<boolean> {
    try {
      if (choice === 'discard') {
        this.handlers().forEach(h => h.discard?.());
        return true;
      }
      if (choice === 'save') {
        this.saving.set(true);
        const results = await Promise.all(this.handlers().filter(h => h.save).map(h => h.save!()));
        return results.length > 0 && results.every(Boolean);
      }
      return false;
    } catch {
      // A rejected handler is an unsuccessful save, never permission to leave.
      return false;
    } finally {
      this.saving.set(false);
      this.prompt.set(false);
      this.pending = null;
      this.resolveChoice = null;
    }
  }

  private handlers(): UnsavedChangesHandler[] {
    return [...this.saveHandlers];
  }

  private hasDiscardHandler(): boolean {
    return this.handlers().some((h) => !!h.discard);
  }
}

/** Router exit (browser back, header links, dashboard). In-page navigation is gated at its own call sites. */
export const unsavedClusterChangesGuard: CanDeactivateFn<unknown> = () =>
  inject(UnsavedChangesGuardService).confirmLeave();
